import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, PencilLine, Check, Trash, Eye, Palette, Translate, LockKey } from "@phosphor-icons/react";
import { renderCertificateHTML, CERT_DESIGNS } from "@/lib/certificateTemplates";
import { LANGUAGES, setLanguage } from "@/i18n";

export default function Settings() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const { i18n, t } = useTranslation();
  const [signature, setSignature] = useState("");  const [directorName, setDirectorName] = useState("");
  const [previewTier, setPreviewTier] = useState("gold");
  const [previewLang, setPreviewLang] = useState("Spanish");
  const [design, setDesign] = useState("classic");
  const canManage = ["super_admin", "director"].includes(user?.role);

  useEffect(() => {
    api.get("/settings/director_signature").then(({ data }) => setSignature(data?.value || ""));
    api.get("/settings/director_name").then(({ data }) => setDirectorName(data?.value || ""));
    api.get("/settings/certificate_design").then(({ data }) => setDesign(data?.value || "classic"));
  }, []);

  const saveDesign = async (key) => {
    try {
      await api.put("/settings/certificate_design", { value: key });
      setDesign(key);
      toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} template activated`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openPreview = async (overrideDesign) => {
    const now = new Date().toISOString().slice(0, 10);
    const demoCert = {
      certificate_no: "CERT-PREVIEW-DEMO",
      title: `${previewLang} ${previewTier === "gold" ? "C1" : previewTier === "silver" ? "B1" : previewTier === "platinum" ? "C2" : "A2"} Completion`,
      level: previewTier === "gold" ? "C1" : previewTier === "silver" ? "B1" : previewTier === "platinum" ? "C2" : "A2",
      template: previewTier,
      language: previewLang,
      issued_date: now,
    };
    const html = await renderCertificateHTML(demoCert, "Sample Student", {
      signatureUrl: signature,
      directorName: directorName || user?.name || "Director",
      design: overrideDesign || design,
      uiLang: i18n.language,
    });
    const w = window.open("", "_blank");
    if (!w) return toast.error("Popup blocked — please allow popups to preview");
    w.document.write(html);
    w.document.close();
  };

  const onSignaturePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) return toast.error("Signature image must be under 500KB");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.put("/settings/director_signature", { value: reader.result });
        setSignature(reader.result);
        toast.success("Signature saved");
      } catch (err) { toast.error(formatApiError(err)); }
    };
    reader.readAsDataURL(file);
  };

  const clearSignature = async () => {
    try {
      await api.put("/settings/director_signature", { value: "" });
      setSignature(""); toast.success("Signature cleared");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveDirectorName = async () => {
    try {
      await api.put("/settings/director_name", { value: directorName });
      toast.success("Director name saved");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("settings.title")}</div>
        <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("settings.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("settings.subtitle")}</p>
      </div>

      <Card className="border-border/60" data-testid="settings-language-card"><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Translate size={18} weight="duotone" />
          <h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>{t("settings.language")}</h3>
        </div>
        <p className="text-xs text-muted-foreground">{t("settings.languageDescription")}</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {LANGUAGES.map((lng) => {
            const active = i18n.language === lng.code;
            return (
              <button
                key={lng.code}
                data-testid={`settings-lang-${lng.code}`}
                onClick={async () => {
                  setLanguage(lng.code);
                  try {
                    await api.patch("/auth/preferences", { ui_language: lng.code });
                    toast.success(t("settings.preferencesSaved"));
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
                className={`flex items-center gap-3 rounded-md border p-3 transition-all text-left ${active ? "border-primary bg-primary/5 ring-1 ring-primary/40" : "border-border hover:border-foreground/30"}`}
              >
                <span aria-hidden className="text-2xl leading-none">{lng.flag}</span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{lng.native}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{lng.code}</div>
                </div>
                {active && <Check size={16} weight="bold" className="ml-auto text-primary" />}
              </button>
            );
          })}
        </div>
      </CardContent></Card>

      <Card className="border-border/60"><CardContent className="p-6 space-y-4">
        <div>
          <div className="text-sm font-semibold">{t("settings.profile")}</div>
          <div className="text-xs text-muted-foreground">{user?.name} · {user?.email}</div>
        </div>
        <div className="flex items-center justify-between border-t border-border pt-4">
          <div>
            <div className="text-sm font-semibold">{t("settings.appearance")}</div>
            <div className="text-xs text-muted-foreground">{theme}</div>
          </div>
          <Button variant="outline" onClick={toggle} className="rounded-full" data-testid="theme-switch-btn">
            {theme === "dark" ? "☀️" : "🌙"}
          </Button>
        </div>
      </CardContent></Card>

      <ChangePasswordCard />

      {canManage && (
        <Card className="border-border/60"><CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <PencilLine size={18} weight="duotone" />
            <h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>Certificate Signature</h3>
          </div>
          <p className="text-xs text-muted-foreground">Upload a signature image and set a director name. These appear on the certificate PDF footer.</p>

          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Director Name</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="director-name-input" value={directorName} onChange={(e) => setDirectorName(e.target.value)}
                placeholder="e.g. Elena Rodriguez" className="h-10" />
              <Button data-testid="director-name-save" onClick={saveDirectorName} className="gap-2"><Check size={14} /> Save</Button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Signature Image</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <label htmlFor="sig-input" data-testid="signature-upload-label"
                className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-md py-4 px-4 cursor-pointer hover:border-foreground/30 transition-colors">
                <UploadSimple size={18} weight="duotone" />
                <span className="text-sm font-semibold">Click to upload PNG</span>
                <span className="text-[10px] text-muted-foreground">Max 500KB, transparent PNG best</span>
                <input id="sig-input" data-testid="signature-input" type="file" accept="image/png,image/svg+xml,image/jpeg" onChange={onSignaturePick} className="hidden" />
              </label>
              {signature && (
                <Button variant="outline" size="icon" onClick={clearSignature} data-testid="signature-clear" className="text-destructive">
                  <Trash size={14} />
                </Button>
              )}
            </div>
            {signature && (
              <div className="mt-3 p-4 rounded-md border border-border bg-gradient-to-br from-[#FFF8DC] to-[#F4DE9C]">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Preview</div>
                <img data-testid="signature-preview" src={signature} alt="Signature" className="max-h-16" />
              </div>
            )}
          </div>

          <div className="border-t border-border pt-4">
            <Label className="text-xs uppercase tracking-wider font-semibold">Preview Certificate</Label>
            <p className="text-[11px] text-muted-foreground mt-1">See how a real certificate will look with your signature — pick a tier and course language.</p>
            <div className="flex flex-wrap items-end gap-3 mt-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tier</Label>
                <Select value={previewTier} onValueChange={setPreviewTier}>
                  <SelectTrigger data-testid="preview-tier-select" className="mt-1 h-10 w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["bronze", "silver", "gold", "platinum"].map((tier) =>
                      <SelectItem key={tier} value={tier} className="capitalize">{tier}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Language</Label>
                <Select value={previewLang} onValueChange={setPreviewLang}>
                  <SelectTrigger data-testid="preview-lang-select" className="mt-1 h-10 w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["English", "Spanish", "French", "German", "Italian", "Mandarin", "Japanese", "Russian"].map((l) =>
                      <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="preview-cert-btn" onClick={openPreview} className="rounded-full gap-2 h-10">
                <Eye size={14} weight="duotone" /> Preview certificate
              </Button>
            </div>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}

function ChangePasswordCard() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error(t("auth.register.errors.passwordShort"));
    if (next !== confirm) return toast.error(t("auth.register.errors.passwordMismatch"));
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success(t("settings.changePassword.success"));
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-border/60" data-testid="change-password-card"><CardContent className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <LockKey size={18} weight="duotone" />
        <h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>{t("settings.changePassword.title")}</h3>
      </div>
      <p className="text-xs text-muted-foreground">{t("settings.changePassword.subtitle")}</p>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold">{t("settings.changePassword.current")}</Label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required
            className="mt-1.5 h-10" data-testid="cp-current" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold">{t("settings.changePassword.newPassword")}</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required
            className="mt-1.5 h-10" data-testid="cp-new" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold">{t("auth.register.fields.confirmPassword")}</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
            className="mt-1.5 h-10" data-testid="cp-confirm" />
        </div>
        <div className="md:col-span-3">
          <Button type="submit" disabled={busy} className="rounded-full gap-2" data-testid="cp-submit">
            <LockKey size={14} weight="bold" />
            {busy ? t("common.saving") : t("settings.changePassword.saveBtn")}
          </Button>
        </div>
      </form>
    </CardContent></Card>
  );
}
